/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react'

import { DueOut } from '../../assets/ts/data/dueout/dueout'
import { Inquiry } from '../../assets/ts/data/inquiry/inquiry'
import { getDaysBetween } from '../../assets/ts/date-and-time/date-and-time'
import { exportToExcel } from '../../assets/ts/general/general'
import Alert from '../alert/alert'
import Buttons from './buttons'

type PageType =
  | 'Archived/Dueouts'
  | 'Archived/Inquiries'
  | 'Completed/Dueouts'
  | 'Completed/Inquiries'
  | 'CustomerDashboard/Dueouts'
  | 'CustomerDashboard/Inquiries'
  | 'Dueouts'
  | 'Inquiries'
  | 'MyInquiries/Dueouts'
  | 'MyInquiries/Inquiries'

interface ExportColumns {
  header: string
  key: string
}
interface Props {
  fetchDataFunc?: () => Promise<DueOut[] | Inquiry[]>
  pageType: PageType
  selectedData?: [] | DueOut[] | Inquiry[]
}

const INQUIRY_PAGE_TYPES: PageType[] = [
  'Archived/Inquiries',
  'Completed/Inquiries',
  'CustomerDashboard/Inquiries',
  'Inquiries',
  'MyInquiries/Inquiries',
]

const ExportToCSVButton = ({ fetchDataFunc, pageType, selectedData }: Props) => {
  const [displayAlert, setDisplayAlert] = useState<boolean>(false)

  const [alertMessage, setAlertMessage] = useState<string>('')

  if (
    (fetchDataFunc === undefined && selectedData === undefined) ||
    (fetchDataFunc !== undefined && selectedData !== undefined)
  ) {
    console.error("Either 'fetchDataFunc' or 'selectedData' should be supplied")
  }

  const determineColumn = (pageType: PageType): ExportColumns[] => {
    const isCustomerView = pageType.includes('CustomerDashboard')

    const inquiryColumnsToExport = [
      { header: 'Inquiry ID', key: 'InquiryID' },
      { header: 'Customer Name', key: 'CustomerFullName' },
      { header: 'PASCODE', key: 'PASCODE' },
      { header: 'Organization', key: 'Organization' },
      { header: 'Assigned Location', key: 'Installation' },
      { header: 'Servicing Organization', key: 'ServicingOrganization' },
      { header: 'Subject', key: 'Subject' },
      { header: 'Section', key: isCustomerView ? 'Section/MetadataValue' : 'SectionValue' },
      { header: 'Category', key: isCustomerView ? 'Category/MetadataValue' : 'CategoryValue' },
      { header: 'SubCategory', key: isCustomerView ? 'SubCategory/MetadataValue' : 'SubCategoryValue' },
      { header: 'Status', key: isCustomerView ? 'Status/StatusDescription' : 'Status' },
      { header: 'Submitted By', key: 'ItemCreatedByFullName' },
      { header: 'Submitted Date', key: 'ItemCreated' },
      { header: 'Date of Last Action', key: 'ItemModified' },
      { header: 'Resolved Date', key: 'ResolvedDate' },
      { header: 'Reopened Date', key: 'ReopenedDate' },
      { header: 'Customer Action Required', key: 'CustomerActionRequiredDate' },
      { header: 'Pending Action Outside CP', key: 'PendingActionOutside' },
      { header: 'Total # of Days', key: 'TotalNumberofDays' },
    ]

    const dueoutColumnsToExport = [
      { header: 'Dueout ID', key: 'DueOutID' },
      { header: 'Customer Name', key: 'CustomerFullName' },
      { header: 'PASCODE', key: 'PASCODE' },
      { header: 'Organization', key: 'Organization' },
      { header: 'Assigned Location', key: 'Installation' },
      { header: 'Servicing Organization', key: 'ServicingBy' },
      { header: 'Subject', key: 'Subject' },
      { header: 'Section', key: isCustomerView ? 'Section/MetadataValue' : 'SectionValue' },
      { header: 'Category', key: isCustomerView ? 'Category/MetadataValue' : 'CategoryValue' },
      { header: 'SubCategory', key: isCustomerView ? 'SubCategory/MetadataValue' : 'SubCategoryValue' },
      { header: 'Status', key: isCustomerView ? 'Status/StatusDescription' : 'Status' },
      { header: 'Submitted By', key: 'ItemCreatedByFullName' },
      { header: 'Submitted Date', key: 'ItemCreated' },
      { header: 'Date of Last Action', key: '' },
      { header: 'Closed Date', key: 'ClosedDate' },
      { header: 'Total # of Days', key: 'TotalNumberofDays' },
    ]

    if (INQUIRY_PAGE_TYPES.includes(pageType)) {
      return inquiryColumnsToExport
    } else {
      return dueoutColumnsToExport
    }
  }

  const determineTitle = (pageType: PageType): 'Due Outs' | 'Inquiries' => {
    const title: 'Due Outs' | 'Inquiries' = `${
      INQUIRY_PAGE_TYPES.includes(pageType) ? 'Inquiries' : 'Due Outs'
    }`
    return title
  }

  const exportToCSV = (data: DueOut[] | Inquiry[], pageType: PageType): void => {
    const inquiryOrDueoutData = data?.map((item) => {
      const { ItemCreated, ItemModified, ResolvedDate } = item
      const NumberofDaystoResolve = getDaysBetween(ItemCreated, ResolvedDate)
      const statusModifiedDate = new Date(ItemModified)
      const differenceInTime = new Date().getTime() - statusModifiedDate.getTime()
      const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24))
      return {
        ...item,
        CustomerFullName: item?.Customer?.properties?.defaultDisplay,
        ItemCreatedByFullName: item?.ItemCreatedBy?.properties?.defaultDisplay,
        NumberofDaystoResolve: NumberofDaystoResolve,
        SubmittedBy: item?.ItemCreatedBy?.properties?.defaultDisplay,
        TotalNumberofDays: differenceInDays,
      }
    })
    exportToExcel(`${determineTitle(pageType)}`, determineColumn(pageType), inquiryOrDueoutData)
  }
  const handleButtonClick = async (): Promise<void> => {
    // If the component user supplied a "fetchDataFunc" that is called to
    // obtain the data.  Otherwise the property "selectedData" is used instead
    const data = fetchDataFunc ? await fetchDataFunc() : selectedData ?? []

    if (data.length > 0) {
      exportToCSV(data, pageType)
    } else {
      setAlertMessage('There are no items selected to export')
      setDisplayAlert(true)
    }
  }
  return (
    <>
      <Buttons
        aria-label='Export to CSV'
        className='btn btn-lg btn-primary ms-auto whitetext text-nowrap border-radius text-uppercase font-0 font-md-12 poppins-semibold'
        icon='icon-exportexcel font-14 me-md-1'
        label='Export to CSV'
        onClick={handleButtonClick}
      />
      {displayAlert && (
        <Alert
          alerttextclass='poppins-semibold font-13 text-center'
          btn1className='btn btn btn-sm font-12 poppins-semibold btn-primary whitetext border-radius text-uppercase'
          btn1iconclassNmae='icon-checked font-12 pe-1'
          btn2classNmae='btn btn btn-border1 darktext btn-sm poppins-semibold font-12 border-radius text-uppercase'
          btn2iconclassNmae='icon-close color-primary font-12 pe-1'
          cancel='Cancel'
          className='alert-info'
          message={alertMessage}
          onClick={() => {
            setAlertMessage('')
            setDisplayAlert(false)
          }}
          yes='OK'
        />
      )}
    </>
  )
}
export default ExportToCSVButton
